import { useReducer, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { RefreshCw } from 'lucide-react';
import { PublicMobileLayout } from '@/layouts/PublicMobileLayout';
import { Button } from '@/components/ui/button';
import { collabApi } from '../api/collab.api';
import { canContinue, cereClase, initialState, photoStats, reducer, toSubmissionBody } from '../hooks/use-collab-submission';
import { StepChooseActivity } from '../components/StepChooseActivity';
import { StepActivityDetails } from '../components/StepActivityDetails';
import { StepDetails } from '../components/StepDetails';
import { StepPhotos } from '../components/StepPhotos';
import { StepDone } from '../components/StepDone';
import { getErrorStatus, getSafeErrorMessage, isApiValidationError } from '@/core/api/response-handler';
import { esteFisierPdf, validaPdfDovada } from '@/domains/lessons/features/practical/practical-evidence';
import { configTip } from '../utils/tipuri-colab';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { useConfirmationDialog } from '@/shared/hooks/useConfirmationDialog';

/** Pana vine numele scolii. Neutru: linkul e si pentru activitati practice, si pentru exercitii cu clasa. */
const TITLU_IMPLICIT = 'Școala Ecoterrienilor';

/**
 * Pagina PUBLICA a linkului de colaborator (spec §3, UX §4.3). Fara cont, fara sidebar, dintr-o mana.
 * Autentificarea e tokenul din URL; un token invalid (inexistent, revocat, din alta editie) da 404
 * uniform, si aratam un singur mesaj, fara sa distingem intre cauze (F5, nu le distinge nici backendul).
 */
export default function CollabActivityPage() {
    const { token = '' } = useParams<{ token: string }>();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [submitting, setSubmitting] = useState(false);

    const info = useQuery({
        queryKey: ['collab', token],
        queryFn: () => collabApi.getInfo(token),
        enabled: token.length > 0,
        retry: false,
    });

    // F3: pozele esuate nu blocheaza trimiterea (utilizatorul poate fi pe 4G, fara nicio
    // iesire daca am bloca) - dar tacerea l-ar face sa creada ca a trimis tot. Confirmare
    // explicita doar cand exista cel putin o poza esuata.
    //
    // `window.confirm` inlocuit cu dialogul aplicatiei: caseta nativa nu respecta tokenii si
    // pragul de 44px, blocheaza firul de executie (deci si canalul CDP, ceea ce ingheata orice
    // harness de masurare) si nu exista in DOM, deci nu se poate verifica pe ecran. Pagina e
    // PUBLICA si folosita de pe teren, de pe telefon — exact contextul in care caseta nativa e
    // cel mai prost randata.
    const stats = photoStats(state);

    const trimite = async () => {
        setSubmitting(true);
        try {
            const lessonAnswerId = await collabApi.submit(token, toSubmissionBody(state));
            dispatch({ type: 'submitted', lessonAnswerId });
        } catch (e) {
            // F4: backendul intoarce 409 si pentru date invalide, si pentru coliziune de
            // concurenta - nu ramificam pe status, aratam mesajul serverului (scris in romana).
            // getSafeErrorMessage (H2, fix round 1): daca `e` NU vine de la backend (cadere de
            // retea inainte sa existe raspuns), `.message` e textul tehnic al lui axios/fetch
            // ("Network Error", in engleza) - nu-l aratam, folosim fallback-ul romanesc.
            if (isApiValidationError(e) && e.fragmenteNerecunoscute?.length) {
                // Clase pe care serverul nu le-a inteles: campul lor e cu un pas inapoi.
                dispatch({ type: 'claseRespinse', fragmente: e.fragmenteNerecunoscute });
                return;
            }
            toast.error(getSafeErrorMessage(e, 'Trimiterea a eșuat. Încearcă din nou.'));
        } finally {
            setSubmitting(false);
        }
    };

    const confirmarePoze = useConfirmationDialog({ onConfirm: trimite });

    const submit = () => {
        if (stats.failed > 0) {
            confirmarePoze.show();
            return;
        }
        void trimite();
    };

    // PDF-ul se verifica local inainte de urcare (tip si 15 MB), ca la profesor: scutim omul de un
    // upload pe date mobile care ar fi sigur refuzat. Pozele le verifica serverul.
    const urca = (file: File, onProgress: (pct: number) => void) => {
        if (esteFisierPdf(file)) {
            const v = validaPdfDovada(file);
            if (!v.valid) return Promise.reject(new Error(v.error ?? 'Fișier invalid'));
        }
        return collabApi.uploadPhoto(token, file, onProgress);
    };

    if (info.isLoading) {
        return <PublicMobileLayout title={TITLU_IMPLICIT}><p className="text-dark/60">Se încarcă…</p></PublicMobileLayout>;
    }
    if (info.isError || !info.data) {
        // H1 (fix round 1): un 404 chiar inseamna link invalid (F5) - dar orice ALTA eroare
        // (cadere de retea, timeout pe 4G, 5xx) NU e "link invalid", e temporara. Fara sa separam
        // cazurile, un profesor pe teren pe semnal slab vedea acelasi mesaj definitiv, fara
        // buton de reincercare, si fie renunta, fie suna degeaba coordonatorul.
        const isNotFound = getErrorStatus(info.error) === 404;
        if (isNotFound) {
            return (
                <PublicMobileLayout title="Link invalid">
                    <p className="text-fluid-base">Acest link nu mai este valid. Cere coordonatorului școlii un link nou.</p>
                </PublicMobileLayout>
            );
        }
        return (
            <PublicMobileLayout title="Eroare de conexiune">
                <div className="flex flex-col items-center gap-4 pt-6 text-center">
                    <p className="text-fluid-base">Nu am putut încărca informațiile despre acest link. Verifică conexiunea și încearcă din nou.</p>
                    <Button type="button" className="min-h-12 w-full" onClick={() => info.refetch()}>
                        <RefreshCw className="size-4" /> Încearcă din nou
                    </Button>
                </div>
            </PublicMobileLayout>
        );
    }

    const chosen = info.data.activities?.find((a) => a.id === state.lessonId);
    const cfg = configTip(state.activityType);

    // `schoolName` e opțional în tipul generat (fără `@NotNull` pe record-ul Java) — o
    // interpolare directă în șablon, `${info.data.schoolName}`, ar afișa literal
    // "undefined" dacă backendul nu-l trimite. Fragilitate cu ancoră, nu observată încă
    // pe backendul real; apărarea urmează modelul din `cod-scoala.api.ts`.
    const numeScoala = typeof info.data.schoolName === 'string' ? info.data.schoolName.trim() : '';
    const titluri = {
        choose: numeScoala || TITLU_IMPLICIT,
        activity: cfg.eticheta,
        details: 'Cine trimite',
        photos: cfg.titluFisiere,
        done: 'Gata',
    } as const;
    const cuInapoi = state.step === 'activity' || state.step === 'details' || state.step === 'photos';

    return (
        <PublicMobileLayout
            title={titluri[state.step]}
            latime={state.step === 'choose' ? 'lata' : 'ingusta'}
            onBack={cuInapoi ? () => dispatch({ type: 'back' }) : undefined}
        >
            {state.step === 'choose' && (
                <>
                    <p className="mb-3 text-sm text-dark">{info.data.editionName} · link pentru {info.data.label}</p>
                    <StepChooseActivity
                        info={info.data}
                        onChoose={(lessonId, activityType) => dispatch({ type: 'chooseActivity', lessonId, activityType })}
                    />
                </>
            )}
            {state.step === 'activity' && state.lessonId && state.activityType && (
                <StepActivityDetails
                    token={token}
                    lessonId={state.lessonId}
                    tip={state.activityType}
                    onContinue={() => dispatch({ type: 'next' })}
                    onBack={() => dispatch({ type: 'back' })}
                />
            )}
            {state.step === 'details' && (
                <StepDetails
                    submittedByName={state.submittedByName}
                    description={state.description}
                    clase={state.clase}
                    numarElevi={state.numarElevi}
                    cereClase={cereClase(state)}
                    fragmenteRespinse={state.fragmenteRespinse}
                    onChange={(v) => dispatch({ type: 'setDetails', ...v })}
                    canContinue={canContinue(state)}
                    onContinue={() => dispatch({ type: 'next' })}
                />
            )}
            {state.step === 'photos' && (
                <StepPhotos
                    photos={state.photos}
                    onChange={(photos) => dispatch({ type: 'setPhotos', photos })}
                    upload={urca}
                    canSubmit={canContinue(state)}
                    submitting={submitting}
                    onSubmit={submit}
                    indicatie={cfg.indicatieFisiere}
                />
            )}
            {state.step === 'done' && (
                <StepDone activityTitle={chosen?.title ?? ''} photoCount={state.photos.filter((p) => p.status === 'done').length}
                          titlu={cfg.titluTrimis}
                          onAnother={() => dispatch({ type: 'reset' })} />
            )}

            <ConfirmationDialog
                isOpen={confirmarePoze.isOpen}
                onClose={confirmarePoze.handleCancel}
                onConfirm={confirmarePoze.handleConfirm}
                title="Trimiți fără toate fișierele?"
                description={`Trimiți ${stats.done} din ${stats.done + stats.failed}? ${
                    stats.failed === 1 ? 'Un fișier nu s-a încărcat.' : `${stats.failed} fișiere nu s-au încărcat.`
                }`}
                confirmText="Trimite"
                cancelText="Înapoi"
                loading={submitting}
            />
        </PublicMobileLayout>
    );
}
